/**
 * Mutaciones local-first de la capa de ORGANIZACIÓN (org_settings/campus/room).
 *
 * Escriben en la BD PowerSync local (Drizzle) → la cola de sync las envía al
 * servidor (`db/sync.rs`), que aplica authz por capacidad (org_rbac: owner/
 * org_admin para ajustes y sedes; campus_admin sobre su propia sede para aulas)
 * y RECHAZA-y-loguea lo no autorizado. El cliente jamás es la autoridad.
 */

import { campus, eq, org_settings, type PowerSyncDrizzleDB, room } from "@space/powersync";
import { nowISO } from "../../utils/date-helpers";
import { generateUUID } from "../../utils/uuid";

// ─── org_settings ─────────────────────────────────────────────────────────────

export interface OrgSettingsInput {
  country?: string;
  timezone?: string;
  locale?: string;
  currency_primary?: string;
  currency_secondary?: string | null;
  exchange_rate?: number | null;
  grading_scheme?: string;
  /** Grupo E: modelo de calificación por defecto de la org (NULL = clave legada). */
  grading_model_id?: string | null;
  attendance_alert_threshold?: number;
  // AUTONOMY: columnas jsonb (viajan como JSON string por PowerSync).
  /** Overrides de features de la org (`{"<key>": boolean}`). */
  features?: string | null;
  /** Marca de la academia (`{"publicName": string, "tagline"?: string}`). */
  branding?: string | null;
}

/**
 * Guarda (crea o actualiza) los ajustes de una organización. `exists` indica si
 * la fila singleton ya está en local (el componente lo sabe por su query): en
 * ese caso hace UPDATE (→ PATCH en sync); si no, INSERT (→ PUT).
 */
export async function saveOrgSettingsMutation(
  db: PowerSyncDrizzleDB,
  orgId: string,
  input: OrgSettingsInput,
  exists: boolean,
): Promise<void> {
  const now = nowISO();
  if (exists) {
    await db
      .update(org_settings)
      .set({ ...input, updated_at: now })
      .where(eq(org_settings.id, orgId));
    return;
  }
  await db.insert(org_settings).values({
    id: orgId,
    country: input.country ?? "VE",
    timezone: input.timezone ?? "America/Caracas",
    locale: input.locale ?? "es",
    currency_primary: input.currency_primary ?? "USD",
    currency_secondary: input.currency_secondary ?? "VES",
    exchange_rate: input.exchange_rate ?? null,
    grading_scheme: input.grading_scheme ?? "ve-0-20",
    grading_model_id: input.grading_model_id ?? null,
    attendance_alert_threshold: input.attendance_alert_threshold ?? 3,
    // AUTONOMY: overrides de features + branding de la academia.
    features: input.features ?? null,
    branding: input.branding ?? null,
    created_at: now,
    updated_at: now,
  });
}

// ─── campus ───────────────────────────────────────────────────────────────────

export interface CampusInput {
  name?: string;
  slug?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  phone?: string | null;
  timezone?: string | null;
  status?: string;
}

/** Crea una sede. Devuelve su id. */
export async function createCampusMutation(
  db: PowerSyncDrizzleDB,
  orgId: string,
  input: CampusInput = {},
): Promise<string> {
  const id = generateUUID();
  const now = nowISO();
  await db.insert(campus).values({
    id,
    organization_id: orgId,
    name: input.name?.trim() || "Sede principal",
    slug: input.slug ?? null,
    address: input.address ?? null,
    city: input.city ?? null,
    state: input.state ?? null,
    phone: input.phone ?? null,
    timezone: input.timezone ?? null,
    status: input.status ?? "active",
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function updateCampusMutation(
  db: PowerSyncDrizzleDB,
  campusId: string,
  updates: CampusInput,
): Promise<void> {
  const patch = { ...updates } as Record<string, unknown>;
  if (typeof patch.name === "string") {
    patch.name = patch.name.trim() || "Sede principal";
  }
  await db
    .update(campus)
    .set({ ...patch, updated_at: nowISO() })
    .where(eq(campus.id, campusId));
}

/** Borrado suave de una sede. */
export async function deleteCampusMutation(
  db: PowerSyncDrizzleDB,
  campusId: string,
): Promise<void> {
  const now = nowISO();
  await db.update(campus).set({ deleted_at: now, updated_at: now }).where(eq(campus.id, campusId));
}

// ─── room ─────────────────────────────────────────────────────────────────────

export interface RoomInput {
  name?: string;
  capacity?: number;
  floor?: string | null;
}

/** Crea un aula en una sede. Devuelve su id. */
export async function createRoomMutation(
  db: PowerSyncDrizzleDB,
  orgId: string,
  campusId: string,
  input: RoomInput = {},
): Promise<string> {
  const id = generateUUID();
  const now = nowISO();
  await db.insert(room).values({
    id,
    campus_id: campusId,
    organization_id: orgId,
    name: input.name?.trim() || "Aula",
    capacity: input.capacity ?? 0,
    floor: input.floor ?? null,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function updateRoomMutation(
  db: PowerSyncDrizzleDB,
  roomId: string,
  updates: RoomInput,
): Promise<void> {
  const patch = { ...updates } as Record<string, unknown>;
  if (typeof patch.name === "string") {
    patch.name = patch.name.trim() || "Aula";
  }
  await db
    .update(room)
    .set({ ...patch, updated_at: nowISO() })
    .where(eq(room.id, roomId));
}

/** Borrado suave de un aula. */
export async function deleteRoomMutation(db: PowerSyncDrizzleDB, roomId: string): Promise<void> {
  const now = nowISO();
  await db.update(room).set({ deleted_at: now, updated_at: now }).where(eq(room.id, roomId));
}
